import React from "react";
import { Container, Button, Typography, Box, Chip, Stack } from "@mui/material";
import { Link as ScrollLink, scroller } from "react-scroll";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import CodeIcon from "@mui/icons-material/Code";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { brand, gradients, shadows } from "../theme";

const scrollTo = (target) =>
  scroller.scrollTo(target, { smooth: true, duration: 800 });

const tags = ["Learn", "Implement", "Innovate", "Impact"];

const highlights = [
  { value: "5,000+", label: "Talent Accelerated" },
  { value: "24+", label: "Partner Campuses" },
  { value: "2", label: "Products Operated" },
  { value: "2018", label: "MSME Registered" },
];

const Home = () => {
  return (
    <section id="home-section" className="home-section">
      <Box
        sx={{
          position: "relative",
          background: gradients.mesh,
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 4, md: 5 },
          pb: { xs: 8, md: 11 },
        }}
      >
        {/* Subtle dotted grid overlay for depth */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)",
            pointerEvents: "none",
          }}
        />

        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Box className="fade-up" sx={{ maxWidth: 900, mx: "auto", textAlign: "center" }}>
            <Chip
              icon={<VerifiedIcon sx={{ color: "#fff !important" }} />}
              label="Founded 2018 · Govt. of India MSME Registered"
              sx={{
                bgcolor: "rgba(255,255,255,0.14)",
                color: "#fff",
                fontWeight: 600,
                mb: 3,
                px: 0.5,
                py: 2.2,
                border: "1px solid rgba(255,255,255,0.22)",
                backdropFilter: "blur(6px)",
              }}
            />

            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.08,
                mb: 2.5,
                fontSize: { xs: "2.25rem", sm: "3rem", md: "3.9rem" },
              }}
            >
              Empowering Institutions Through{" "}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, #ffd9c7, ${brand.orangeLight})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Technology &amp; Innovation
              </Box>
            </Typography>

            <Typography
              component="p"
              sx={{
                fontWeight: 400,
                color: "rgba(255,255,255,0.9)",
                mb: 3.5,
                maxWidth: 660,
                mx: "auto",
                fontSize: { xs: "1.02rem", md: "1.2rem" },
                lineHeight: 1.65,
              }}
            >
              A premium technology consulting, custom software engineering, and
              capability-building partner — bridging academia and industry through
              practical technology enablement.
            </Typography>

            <Stack
              direction="row"
              spacing={1.25}
              flexWrap="wrap"
              useFlexGap
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              {tags.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.10)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.28)",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                onClick={() => scrollTo("contacts-section")}
                variant="contained"
                color="secondary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ boxShadow: shadows.accent, py: 1.4, px: 3.5 }}
              >
                Partner With Us
              </Button>
              <Button
                onClick={() => scrollTo("services-section")}
                variant="outlined"
                size="large"
                startIcon={<CodeIcon />}
                sx={{
                  color: "#fff",
                  py: 1.4,
                  px: 3.5,
                  borderColor: "rgba(255,255,255,0.55)",
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Explore Services
              </Button>
            </Stack>
          </Box>

          {/* Highlight strip — replaces the hero illustration */}
          <Box
            className="fade-up-1"
            sx={{
              mt: { xs: 6, md: 8 },
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 1.5, md: 2.5 },
              maxWidth: 860,
              mx: "auto",
            }}
          >
            {highlights.map((h) => (
              <Box
                key={h.label}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  textAlign: "center",
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(8px)",
                  transition: "transform .25s ease, background .25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    bgcolor: "rgba(255,255,255,0.13)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    lineHeight: 1,
                    color: "#fff",
                  }}
                >
                  {h.value}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.82)",
                    fontSize: { xs: "0.76rem", md: "0.86rem" },
                    mt: 0.75,
                  }}
                >
                  {h.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Scroll cue */}
          <Box sx={{ textAlign: "center", mt: { xs: 5, md: 7 } }}>
            <ScrollLink to="about-section" smooth duration={800}>
              <Box
                className="hero-scroll-cue"
                sx={{
                  display: "inline-flex",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "#fff" },
                }}
              >
                <KeyboardArrowDownIcon fontSize="large" />
              </Box>
            </ScrollLink>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default Home;
